(() => {
  'use strict';
  if (customElements.get('doi-home-today')) return;
  const zone = 'America/Los_Angeles';
  const dayKey = date => {
    const parts = new Intl.DateTimeFormat('en-CA', {timeZone:zone,year:'numeric',month:'2-digit',day:'2-digit'}).formatToParts(date);
    const values = Object.fromEntries(parts.map(part => [part.type,part.value]));
    return `${values.year}-${values.month}-${values.day}`;
  };
  const validData = data => data && data.version === 1 && Array.isArray(data.events) && data.events.every(event => event && typeof event.id === 'string' && typeof event.title === 'string' && Array.isArray(event.dates));
  const todayEvents = (data,key) => {
    const seen = new Set();
    return data.events.filter(event => {
      if (!event.dates.includes(key) || seen.has(event.id)) return false;
      seen.add(event.id);return true;
    }).sort((a,b) => (Number(a.order)||0)-(Number(b.order)||0)||a.title.localeCompare(b.title));
  };
  customElements.define('doi-home-today', class extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      this.grid = this.querySelector('[data-today-grid]');
      this.message = this.querySelector('[data-today-message]');
      this.status = this.querySelector('[data-today-status]');
      this.strings = JSON.parse(this.querySelector('[data-today-strings]').textContent);
      this.lastCheck = Date.now();
      this.openIds = new Set();
      this.more = this.querySelector('[data-today-more]');
      this.moreGrid = this.querySelector('[data-today-more-grid]');
      this.moreLabel = this.querySelector('[data-today-more-label]');
      this.scrollHint = this.querySelector('[data-today-scroll-hint]');
      this.scrollObserver = new ResizeObserver(() => this.measureScroll());
      this.breakpoints = [matchMedia('(max-width: 679px)'), matchMedia('(max-width: 1023px)')];
      this.breakpoints.forEach(query => query.addEventListener('change', () => this.layout(), {signal:this.events.signal}));
      this.more.addEventListener('toggle', () => this.updateMoreLabel(), {signal:this.events.signal});
      this.addEventListener('toggle',event => {
        const card = event.target.closest('[data-event-id]');
        if (card) card.open ? this.openIds.add(card.dataset.eventId) : this.openIds.delete(card.dataset.eventId);
      },{capture:true,signal:this.events.signal});
      try {
        const data = JSON.parse(this.querySelector('[data-today-data]')?.textContent || 'null');
        if (validData(data)) this.data = data;
      } catch {}
      this.render();
      if (!this.data) this.refresh();
      this.timer = setInterval(() => this.check(),60000);
      document.addEventListener('visibilitychange',()=>{if(!document.hidden)this.check();},{signal:this.events.signal});
    }
    text(html) {
      const doc = new DOMParser().parseFromString(String(html || ''),'text/html');
      doc.querySelectorAll('script,style').forEach(node=>node.remove());
      doc.querySelectorAll('br').forEach(node=>node.replaceWith(doc.createTextNode(' · ')));
      return doc.body.textContent.replace(/\s+/g,' ').trim();
    }
    check() {
      if (document.hidden) return;
      if (this.key !== dayKey(new Date())) {this.openIds.clear();this.more.open=false;this.render();this.refresh();}
      else if (Date.now()-this.lastCheck >= 300000) this.refresh();
    }
    async refresh() {
      if (this.pending || !this.dataset.source) return;
      const url = new URL(this.dataset.source,location.origin);
      if (url.origin !== location.origin) return;
      this.pending = true;this.lastCheck = Date.now();
      const controller = new AbortController();this.request = controller;
      const timeout = setTimeout(()=>controller.abort(),10000);
      try {
        url.searchParams.set('doi_today_refresh',Math.floor(Date.now()/300000));
        const response = await fetch(url,{cache:'no-store',credentials:'same-origin',signal:controller.signal});
        if (!response.ok) throw new Error('Events unavailable');
        const doc = new DOMParser().parseFromString(await response.text(),'text/html');
        const data = JSON.parse(doc.querySelector('[data-weekly-data]')?.textContent || 'null');
        if (!validData(data)) throw new Error('Invalid event data');
        if (this.isConnected && JSON.stringify(data)!==JSON.stringify(this.data)) {this.data=data;this.render();}
      } catch {
        if (this.isConnected && !this.data) {this.message.textContent=this.strings.unavailable;this.message.hidden=false;this.status.textContent=this.strings.unavailable;}
      } finally {clearTimeout(timeout);this.pending=false;}
    }
    render() {
      this.key = dayKey(new Date());
      const date = new Date(this.key+'T12:00:00Z');
      const time = this.querySelector('[data-today-date]');
      time.dateTime=this.key;time.textContent=new Intl.DateTimeFormat('en-US',{timeZone:zone,weekday:'long',month:'long',day:'numeric'}).format(date);
      if (!this.data) return;
      const events = todayEvents(this.data,this.key);
      const focused = document.activeElement?.closest('[data-event-id]');
      const focusedId = focused && this.contains(focused) ? focused.dataset.eventId : null;
      const focusWasSummary = focusedId && document.activeElement.tagName === 'SUMMARY';
      if (focusedId && !focusWasSummary) {this.deferredRender=true;this.addEventListener('focusout',()=>{if(this.deferredRender){this.deferredRender=false;this.render();}},{once:true,signal:this.events.signal});return;}
      const moreFocused = document.activeElement === this.more.querySelector('summary');
      const fragment = document.createDocumentFragment();
      for (const event of events) {
        const card=document.createElement('details');card.className='dtf-card';card.dataset.eventId=event.id;card.open=this.openIds.has(event.id);
        const summary=document.createElement('summary'),copy=document.createElement('span'),meta=document.createElement('small'),title=document.createElement('strong');
        const metadata=this.text(event.meta);const when=metadata.split(' · ')[0];
        meta.textContent=[when,event.city].filter(Boolean).join(' · ');title.textContent=event.title;copy.append(meta,title);summary.append(copy,this.querySelector('[data-today-icon]').content.cloneNode(true));
        const body=document.createElement('div');body.className='dtf-card-body';
        const description=document.createElement('p');description.textContent=this.text(event.description);
        const fullMeta=document.createElement('p');fullMeta.className='dtf-meta';fullMeta.textContent=metadata;
        const links=document.createElement('div');links.className='dtf-links';
        const parsed=new DOMParser().parseFromString(String(event.links||''),'text/html');
        for(const source of parsed.querySelectorAll('a[href]')) {
          let url;try{url=new URL(source.getAttribute('href'),location.origin);}catch{continue;}
          if(!['https:','http:'].includes(url.protocol))continue;
          const link=document.createElement('a');link.href=url.href;link.textContent=source.textContent.trim()||this.strings.details;
          if(url.origin!==location.origin){link.target='_blank';link.rel='noopener noreferrer';}
          links.append(link);
        }
        body.append(description,fullMeta,links);card.append(summary,body);fragment.append(card);
      }
      this.moreGrid.replaceChildren();
      this.grid.replaceChildren(fragment);
      this.layout();
      if(moreFocused && !this.more.hidden)this.more.querySelector('summary').focus({preventScroll:true});
      const label=this.strings.events.replace('[total]',events.length);
      this.querySelector('[data-today-count]').textContent=events.length?label:'';
      this.message.hidden=events.length>0;
      this.message.textContent=this.data.coverageThrough<this.key?this.strings.refreshing:this.strings.empty;
      this.status.textContent=events.length?label:this.message.textContent;
      if(focusedId){const next=[...this.querySelectorAll('[data-event-id]')].find(card=>card.dataset.eventId===focusedId);(next?.querySelector('summary')||this.querySelector('.dtf-footer a')).focus({preventScroll:true});}
    }
    updateMoreLabel() {
      const total = this.moreGrid.children.length;
      this.moreLabel.textContent = (this.more.open ? this.strings.less : this.strings.more).replace('[total]', total);
    }
    layout() {
      const cards = [...this.grid.children, ...this.moreGrid.children];
      const mobile = this.breakpoints[0].matches;
      const limit = mobile ? cards.length : this.breakpoints[1].matches ? 6 : 9;
      const active = document.activeElement;
      const activeCard = active?.closest('[data-event-id]');
      const activeIndex = cards.indexOf(activeCard);
      const summaryFocused = active === this.more.querySelector('summary');
      cards.forEach((card,index) => (index < limit ? this.grid : this.moreGrid).append(card));
      this.more.hidden = cards.length <= limit;
      if(activeIndex >= limit)this.more.open = true;
      if(this.more.hidden)this.more.open = false;
      this.updateMoreLabel();
      const scrollable = mobile && cards.length > 3;
      this.grid.toggleAttribute('data-scrollable', scrollable);
      this.scrollHint.hidden = !scrollable;
      this.scrollHint.textContent = this.strings.swipe.replace('[total]', cards.length);
      this.scrollObserver.disconnect();
      if (scrollable) {
        this.grid.tabIndex = 0;
        this.grid.setAttribute('role', 'region');
        this.grid.setAttribute('aria-labelledby', this.getAttribute('aria-labelledby'));
        this.grid.setAttribute('aria-describedby', this.scrollHint.id);
        this.scrollObserver.observe(this.grid);
        cards.slice(0, 3).forEach(card => this.scrollObserver.observe(card.querySelector('summary')));
        this.measureScroll();
      } else {
        this.grid.removeAttribute('tabindex');
        this.grid.removeAttribute('role');
        this.grid.removeAttribute('aria-labelledby');
        this.grid.removeAttribute('aria-describedby');
        this.grid.style.removeProperty('--dtf-scroll-height');
      }
      if(activeCard && activeCard.contains(active))active.focus({preventScroll:true});
      else if(summaryFocused && this.more.hidden)(cards[0]?.querySelector('summary') || this.querySelector('.dtf-footer a')).focus({preventScroll:true});
    }
    measureScroll() {
      if (!this.grid.hasAttribute('data-scrollable')) return;
      const cards = [...this.grid.children].slice(0, 3);
      const style = getComputedStyle(this.grid);
      const height = cards.reduce((total, card) => {
        const border = getComputedStyle(card);
        return total + card.querySelector('summary').getBoundingClientRect().height + (parseFloat(border.borderTopWidth) || 0) + (parseFloat(border.borderBottomWidth) || 0);
      }, 0) + (parseFloat(style.rowGap) || 0) * 2 + (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);
      const value = Math.ceil(height) + 'px';
      if (this.grid.style.getPropertyValue('--dtf-scroll-height') !== value) this.grid.style.setProperty('--dtf-scroll-height', value);
    }
    disconnectedCallback() {this.events?.abort();this.events=null;this.scrollObserver?.disconnect();clearInterval(this.timer);this.request?.abort();}
  });
})();


